import {
  camelCaseToSpaces,
  capitalizeFirstLetter,
  isObject,
} from "../../utils";
import _ from "lodash";

/**
 * Derive a stable set of column definitions from data.
 * This is used as a fallback in some layouts (ex: NewTableLayout) and should
 * be predictable, fast, and TanStack v8 compatible.
 */
export function renderColumnDefs(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return [];

  // Collect union of keys across rows (ignore non-objects safely)
  const keySet = new Set();
  arr.forEach((row) => {
    if (!row || typeof row !== "object" || Array.isArray(row)) return;
    Object.keys(row).forEach((k) => keySet.add(k));
  });

  const keys = Array.from(keySet);

  const derived = keys.map((key) => {
    // Hide private/internal keys by default
    const isHiddenByDefault = key.charAt(0) === "_";

    return {
      id: key,
      accessorKey: key,
      enableHiding: true,
      header: camelCaseToSpaces(key),
      ...(isHiddenByDefault ? { visible: false } : {}),
      // Keep cells simple; callers can override via renderCustomColumnDefs
      cell: (info) => {
        const value = info.getValue?.();
        if (typeof value === "boolean") return value.toString();
        if (isObject(value) || Array.isArray(value)) {
          try {
            return JSON.stringify(value);
          } catch (e) {
            return String(value);
          }
        }
        return value ?? "";
      },
    };
  });

  return renderCustomColumnDefs(derived);
}

export function renderCustomColumnDefs(arr, id) {
  const res = arr || [];

  return res
    .filter((item) => {
      const shouldRemove =
        typeof item.remove === "function" ? !!item.remove() : !!item.remove;
      return !shouldRemove;
    })
    .map((item, i) => {
      if (typeof item === "string") {
        return {
          enableHiding: true,
          accessorKey: item,
          header: camelCaseToSpaces(item),
          id: item,
        };
      }

      return {
        ...item,
        size: item.width || item.size,
        minSize: item.minWidth || item.minSize,
        cell: item.cell || item.Cell,
        header:
          typeof item.header === "function" || typeof item.Header === "function"
            ? item.header || item.Header
            : camelCaseToSpaces(
                item.Header || item.header || item.id || item.accessorKey
              ) ||
              item.header ||
              item.Header ||
              item.id,
        accessorKey: item.accessor || item.accessorKey || item.id,
        meta: { editable: item.editable },
      };
    });
}

export function renderAllColumns(cols, data) {
  const fullColDefs = renderColumnDefs(data).map((item) => ({
    ...item,
    size: item.width || item.size,
    minSize: item.minWidth || item.minSize,
    accessorKey: item.accessor || item.id,
    visible: false,
  }));
  const customColDefs = cols;

  const res = _.differenceBy(fullColDefs, customColDefs, "accessorKey");

  return [...res, ...customColDefs];
}

// export function renderDefaultVisible(arr) {
//   return arr.map((key) => {
//     return isObject(key) && key.visible && key.id;
//   });
// }

export function renderValidations(arr) {
  const data = arr;
  const res = {};
  data.forEach((item) => {
    if (isObject(item)) {
      const id = item.id;
      return (
        id &&
        (item.required || item.custom || item.pattern || item.validations) &&
        (res[id] = {
          ...item,
        })
      );
    }
  });

  return res;
}

export function renderDefaultVisible(data) {
  const hidden = data
    .filter((col) => {
      if (typeof col?.visible === "boolean" && !col.visible) {
        return true;
      }
      return false;
    })
    .map((col) => col?.accessorKey || col?.accessor || col?.id)
    .filter(Boolean)
    .reduce((a, v) => ({ ...a, [v]: false }), {});
  return hidden;
}

export function renderDefaultSelected(data, options) {
  if (options === "all") {
    return Array.from(Array(data.length).keys()).reduce((acc, current) => {
      return (acc = { ...acc, [current]: true });
    }, {});
  }
  return;
}

export function renderLabelRequired(label) {
  return (
    <div>
      {label} - <span className="text-red-500">Required</span>
    </div>
  );
}

export function renderLabelOptional() {}
